/**
 * HeatmapCell Component
 * WP-RETIRE-2, WP-RETIRE-5: Individual cell with tooltip showing detailed projection info
 * Uses FundingLevel (pre_retirement, well_funded, adequate, underfunded)
 */

import Tooltip from '@/components/ui/Tooltip';
import { formatCurrencyCompact } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { ProjectionCell, FundingLevel } from '@/utils/retirementCalculations';

interface HeatmapCellProps {
  cell: ProjectionCell;
}

// Color mapping for funding status (WP-RETIRE-5, WP-RETIRE-11)
const FUNDING_COLORS: Record<FundingLevel, { bg: string; text: string; label: string }> = {
  pre_retirement: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Pre-Retirement' },
  well_funded: { bg: 'bg-green-500/20', text: 'text-green-800', label: 'Well Funded' },
  adequate: { bg: 'bg-yellow-500/20', text: 'text-yellow-800', label: 'Adequate' },
  underfunded: { bg: 'bg-red-500/20', text: 'text-red-800', label: 'Underfunded' },
};

export default function HeatmapCell({ cell }: HeatmapCellProps) {
  const {
    age,
    returnRate,
    projectedWealthCents,
    fundingLevel,
    yearsCovered,
    yearsRemaining,
    coverageRatio,
  } = cell;
  const colors = FUNDING_COLORS[fundingLevel];

  const isPreRetirement = fundingLevel === 'pre_retirement';

  const tooltipContent = (
    <div className="p-2 text-sm min-w-[200px]">
      <div className="font-medium mb-2">
        Age {age} with {(returnRate * 100).toFixed(0)}% Real Return
      </div>
      <div className="space-y-1 text-gray-200">
        <div>Projected Wealth: {formatCurrencyCompact(projectedWealthCents)}</div>
        <div className="mt-2">
          <span className="font-medium">
            Status: {isPreRetirement ? 'Pre-Retirement (accumulating)' : colors.label}
          </span>
        </div>
        {isPreRetirement ? (
          <ul className="list-disc ml-4 text-xs">
            <li>Projected net worth if saving continues at current rate</li>
          </ul>
        ) : (
          <ul className="list-disc ml-4 text-xs">
            <li>
              {yearsCovered === Infinity
                ? 'Portfolio sustains indefinitely at 2% real growth'
                : `Covers ${yearsCovered.toFixed(1)} years of net expenses (at 2% growth)`}
            </li>
            <li>
              Need coverage to age 95 ({yearsRemaining} yr)
            </li>
            <li>
              {coverageRatio >= 999
                ? 'Coverage: Fully funded'
                : `Coverage ratio: ${(coverageRatio * 100).toFixed(0)}%`}
            </li>
          </ul>
        )}
      </div>
    </div>
  );

  const ariaLabel = isPreRetirement
    ? `Age ${age}, ${(returnRate * 100).toFixed(0)}% return: ${formatCurrencyCompact(projectedWealthCents)}, Pre-retirement projected net worth`
    : `Age ${age}, ${(returnRate * 100).toFixed(0)}% return: ${formatCurrencyCompact(projectedWealthCents)}, ${colors.label}`;

  return (
    <td
      className={cn(
        'ring-1 ring-inset ring-white text-center',
        colors.bg,
        colors.text
      )}
    >
      <Tooltip content={tooltipContent} position="top">
        <div
          className="py-2 text-center text-sm font-medium cursor-default focus:outline-none focus:ring-2 focus:ring-primary-500"
          tabIndex={0}
          role="gridcell"
          aria-label={ariaLabel}
        >
          {formatCurrencyCompact(projectedWealthCents)}
        </div>
      </Tooltip>
    </td>
  );
}
